import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { addNotification } from "services/notifications/notificationsSlice";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

export const apiPublicSlice = createApi({
  reducerPath: "apiPublicV2",
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl
  }),

  tagTypes: ["notesPublic"],

  endpoints: (builder) => ({
    getPublicInfo: builder.query({
      query: ({ id }) => `/api/v2/shared_target_lists/${id}`
    }),
    getMarketMapData: builder.query({
      query: ({ jobId, token }) =>
        `/api/v2/jobs/${jobId}/market_intel?shared_target_list_id=${token}`,
      transformResponse: (response: { market_intel: any }) =>
        response.market_intel
    }),
    getCandidates: builder.query({
      query: ({ id }) => `/api/v2/shared_target_lists/${id}/candidates`
    }),
    getSharedConnections: builder.query({
      query: ({ accessToken, attorneyId }) =>
        `/api/v2/attorneys/${attorneyId}/shared_connections?shared_target_list_id=${accessToken}`
    }),
    postPartnerResponse: builder.mutation({
      query: ({ sharedTargetListId, attorneyId, status }) => ({
        url: `/api/v2/shared_target_lists/${sharedTargetListId}/partner_responses`,
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          partner_responses: [
            {
              attorney_id: attorneyId,
              status
            }
          ]
        })
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Response sent successfully",
              type: "success"
            })
          );
        } catch (error) {
          dispatch(
            addNotification({
              message: "Failed to send response",
              type: "danger"
            })
          );
        }
      }
    }),
    postSubmitSharedTargetList: builder.mutation({
      query: ({ token }) => ({
        url: `/api/v2/shared_target_lists/${token}/complete_review`,
        method: "POST"
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Shared target list submitted successfully",
              type: "success"
            })
          );
        } catch (error) {
          dispatch(
            addNotification({
              message: "Failed to submit shared target list",
              type: "danger"
            })
          );
        }
      }
    }),

    getTargetPublicNotes: builder.query({
      providesTags: ["notesPublic"],
      query: ({ targetId, id }) =>
        `/targets/${targetId}/target_notes?shared_target_list_id=${id}`
    }),
    postTargetPublicNote: builder.mutation({
      invalidatesTags: ["notesPublic"],
      query: ({ targetId, text, pinned, id }) => ({
        url: `/targets/${targetId}/target_notes?shared_target_list_id=${id}`,
        method: "POST",
        body: {
          target_note: {
            text,
            pinned
          }
        }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Note added successfully",
              type: "success"
            })
          );
        } catch {
          dispatch(
            addNotification({
              message: "Failed to add note",
              type: "danger"
            })
          );
        }
      }
    }),
    patchTargetPublicNote: builder.mutation({
      invalidatesTags: ["notesPublic"],
      query: ({ targetId, noteId, text, pinned, id }) => ({
        url: `/targets/${targetId}/target_notes/${noteId}?shared_target_list_id=${id}`,
        method: "PATCH",
        body: {
          target_note: {
            text,
            pinned
          }
        }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Note updated successfully",
              type: "success"
            })
          );
        } catch {
          dispatch(
            addNotification({
              message: "Failed to update note",
              type: "danger"
            })
          );
        }
      }
    }),
    deleteTargetPublicNote: builder.mutation({
      invalidatesTags: ["notesPublic"],
      query: ({ targetId, noteId, id }) => ({
        url: `/targets/${targetId}/target_notes/${noteId}?shared_target_list_id=${id}`,
        method: "DELETE"
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Note deleted successfully",
              type: "success"
            })
          );
        } catch {
          dispatch(
            addNotification({
              message: "Failed to delete note",
              type: "danger"
            })
          );
        }
      }
    }),

    // Public Candidate Notes endpoints
    getPublicCandidateNotes: builder.query({
      providesTags: ["notesPublic"],
      query: ({ attorneyId, jobId, sharedTargetListId }) =>
        `/api/v2/candidate_notes?attorney_id=${attorneyId}&job_id=${jobId}&shared_target_list_id=${sharedTargetListId}`
    }),
    postPublicCandidateNote: builder.mutation({
      invalidatesTags: ["notesPublic"],
      query: ({ candidate_note, attorneyId, jobId, sharedTargetListId }) => ({
        url: `/api/v2/candidate_notes?attorney_id=${attorneyId}&job_id=${jobId}&shared_target_list_id=${sharedTargetListId}`,
        method: "POST",
        body: { candidate_note }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Note added successfully",
              type: "success"
            })
          );
        } catch {
          dispatch(
            addNotification({
              message: "Failed to add note",
              type: "danger"
            })
          );
        }
      }
    }),
    updatePublicCandidateNote: builder.mutation({
      invalidatesTags: ["notesPublic"],
      query: ({
        id,
        candidate_note,
        attorneyId,
        jobId,
        sharedTargetListId
      }) => ({
        url: `/api/v2/candidate_notes/${id}?attorney_id=${attorneyId}&job_id=${jobId}&shared_target_list_id=${sharedTargetListId}`,
        method: "PATCH",
        body: { candidate_note }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Note updated successfully",
              type: "success"
            })
          );
        } catch {
          dispatch(
            addNotification({
              message: "Failed to update note",
              type: "danger"
            })
          );
        }
      }
    }),
    deletePublicCandidateNote: builder.mutation({
      invalidatesTags: ["notesPublic"],
      query: ({ id, attorneyId, jobId, sharedTargetListId }) => ({
        url: `/api/v2/candidate_notes/${id}?attorney_id=${attorneyId}&job_id=${jobId}&shared_target_list_id=${sharedTargetListId}`,
        method: "DELETE"
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Note deleted successfully",
              type: "success"
            })
          );
        } catch {
          dispatch(
            addNotification({
              message: "Failed to delete note",
              type: "danger"
            })
          );
        }
      }
    })
  }),
  refetchOnMountOrArgChange: true
});

// Export hooks for usage in functional components
export const {
  useGetPublicInfoQuery,
  useGetMarketMapDataQuery,
  useGetCandidatesQuery,
  useGetSharedConnectionsQuery,
  usePostPartnerResponseMutation,
  usePostSubmitSharedTargetListMutation,
  useGetTargetPublicNotesQuery,
  useDeleteTargetPublicNoteMutation,
  usePatchTargetPublicNoteMutation,
  usePostTargetPublicNoteMutation,
  // Public Candidate Notes hooks
  useGetPublicCandidateNotesQuery,
  usePostPublicCandidateNoteMutation,
  useUpdatePublicCandidateNoteMutation,
  useDeletePublicCandidateNoteMutation
} = apiPublicSlice;
